import React, { useState } from 'react';
import { rasService } from '../services/rasService';
import { ProcessingParams, ApiResponse } from '../types/api';

const DataProcessingPanel: React.FC = () => {
  const [params, setParams] = useState<ProcessingParams>({
    id: '',
    event_id: '',
    horse_id: '',
    meeting_id: '',
    date: '',
    force_process: false,
    is_ignored: false,
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ApiResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleProcess = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const processParams = Object.keys(params).reduce((acc, key) => {
        const value = params[key as keyof ProcessingParams];
        // Always include boolean fields (force_process, is_ignored) regardless of their value
        if (key === 'force_process' || key === 'is_ignored') {
          (acc as any)[key] = value;
        } else if (value !== '' && value !== false && value !== null && value !== undefined) {
          (acc as any)[key] = value;
        }
        return acc;
      }, {} as ProcessingParams);

      const response = await rasService.processRaceData(processParams);
      setResult(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during processing');
    } finally {
      setLoading(false);
    }
  };


  const handleChange = (field: keyof ProcessingParams, value: any) => {
    setParams(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div>
      {/* <h2 className="panel-title">Data Processing</h2> */}

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '30px' }}>
        
        {/* Custom Processing */}
        <div style={{ padding: '20px', border: '1px solid #e9ecef', borderRadius: '8px', backgroundColor: '#f8f9fa', maxWidth: '600px', width: '100%' }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#495057' }}>Data Processing</h3>
          
          <form onSubmit={handleProcess}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Record ID (optional):
                </label>
                <input
                  type="text"
                  placeholder="Specific record ID to process"
                  value={params.id || ''}
                  onChange={(e) => handleChange('id', e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Event ID (optional):
                </label>
                <input
                  type="text"
                  placeholder="Specific event ID to process"
                  value={params.event_id || ''}
                  onChange={(e) => handleChange('event_id', e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Horse ID (optional):
                </label>
                <input
                  type="text"
                  placeholder="Specific horse ID to process"
                  value={params.horse_id || ''}
                  onChange={(e) => handleChange('horse_id', e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Meeting ID (optional):
                </label>
                <input
                  type="text"
                  placeholder="Specific meeting ID to process"
                  value={params.meeting_id || ''}
                  onChange={(e) => handleChange('meeting_id', e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Date (optional):
                </label>
                <input
                  type="date"
                  value={params.date || ''}
                  onChange={(e) => handleChange('date', e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={params.force_process || false}
                    onChange={(e) => handleChange('force_process', e.target.checked)}
                  />
                  <span style={{ fontWeight: '500' }}>Force Process (ignore previous processing)</span>
                </label>
                
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={params.is_ignored || false}
                    onChange={(e) => handleChange('is_ignored', e.target.checked)}
                  />
                  <span style={{ fontWeight: '500' }}>Include ignored</span>
                </label>
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '12px 16px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500',
                  opacity: loading ? 0.6 : 1,
                }}
              >
                {loading ? (
                  <>
                    <span className="loading-spinner" />
                    Processing...
                  </>
                ) : (
                  'Process Data'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Processing Results */}
      {result && (
        <div style={{ padding: '20px', border: '1px solid #e9ecef', borderRadius: '8px', backgroundColor: '#ffffff' }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#495057' }}>Processing Result</h3>
          
          {result.error ? (
            <div className="error-message">
              <strong>Processing Failed:</strong> {result.error}
            </div>
          ) : (
            <div className="success-message">
              Processing completed successfully!
            </div>
          )}

          <details style={{ marginTop: '15px' }}>
            <summary style={{ cursor: 'pointer', fontWeight: '500', marginBottom: '10px' }}>
              View Full Response
            </summary>
            <pre style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '15px', 
              borderRadius: '4px', 
              overflow: 'auto',
              fontSize: '0.9rem',
              border: '1px solid #e9ecef'
            }}>
              {JSON.stringify(result, null, 2)}
            </pre>
          </details>
        </div>
      )}

      {/* Processing Status Indicator */}
      {loading && (
        <div style={{ 
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#007bff',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '8px',
          boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
          zIndex: 1000
        }}>
          <span className="loading-spinner" />
          Processing data...
        </div>
      )}

    </div>
  );
};

export default DataProcessingPanel;